import gsap from "gsap";

import { playCashSound, playCoinTick } from "./audio";

type CashTier = "common" | "uncommon" | "rare" | "epic" | "legendary";

const TIER_COUNTS: Record<
  CashTier,
  { coins: number; bills: number; color: string }
> = {
  common: { coins: 1, bills: 0, color: "#ffffff" },
  uncommon: { coins: 2, bills: 0, color: "#19d500" },
  rare: { coins: 4, bills: 1, color: "#00b2ff" },
  epic: { coins: 6, bills: 2, color: "#b22eff" },
  legendary: { coins: 8, bills: 3, color: "#ffe500" },
};

export function splitCashCents(totalCents: number, parts: number): number[] {
  if (totalCents <= 0 || parts <= 0) return [];
  const base = Math.floor(totalCents / parts);
  const values = Array.from({ length: parts }, () => base);
  values[values.length - 1] += totalCents - base * parts;
  return values;
}

function cashTier(amountCents: number, packPriceCents: number): CashTier {
  if (packPriceCents <= 0) return "common";
  const ratio = amountCents / packPriceCents;
  if (ratio < 0.4) return "common";
  if (ratio < 0.8) return "uncommon";
  if (ratio < 1.5) return "rare";
  if (ratio < 2.5) return "epic";
  return "legendary";
}

export function buildCashFlight(amountCents: number, packPriceCents: number) {
  if (amountCents <= 0) {
    return { kinds: [] as ("coin" | "bill")[], steps: [], color: "#ffffff" };
  }
  const plan = TIER_COUNTS[cashTier(amountCents, packPriceCents)];
  return {
    kinds: Array.from({ length: plan.coins }, (_, index): "coin" | "bill" =>
      index < plan.bills ? "bill" : "coin",
    ),
    steps: splitCashCents(amountCents, plan.coins),
    color: plan.color,
  };
}

function centerOf(element: HTMLElement) {
  const rect = element.getBoundingClientRect();
  return { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 };
}

function cashMotionEvent(phase: string) {
  document.dispatchEvent(
    new CustomEvent("quick-rip:cash-motion", { detail: { phase } }),
  );
}

export function runCashout({
  origin,
  target,
  amountCents,
  packPriceCents,
  reducedMotion,
  onStep,
}: {
  origin: HTMLElement;
  target: HTMLElement;
  amountCents: number;
  packPriceCents: number;
  reducedMotion: boolean;
  onStep?: (cents: number) => void;
}): Promise<void> {
  cashMotionEvent("ka_ching");
  playCashSound();
  if (reducedMotion) {
    cashMotionEvent("count");
    onStep?.(amountCents);
    return Promise.resolve();
  }

  const plan = buildCashFlight(amountCents, packPriceCents);
  const count = plan.kinds.length;
  if (count === 0) return Promise.resolve();
  const steps = plan.steps;
  const start = centerOf(origin);
  const destination = centerOf(target);
  const flash = document.createElement("i");
  flash.className = "quick-rip__cash-flash";
  flash.style.setProperty("--cash-tier-color", plan.color);
  flash.style.left = `${start.x}px`;
  flash.style.top = `${start.y}px`;
  document.body.append(flash);
  gsap.fromTo(
    flash,
    { xPercent: -50, yPercent: -50, scale: 0.35, opacity: 0.9 },
    {
      xPercent: -50,
      yPercent: -50,
      scale: 2,
      opacity: 0,
      duration: 0.48,
      ease: "power2.out",
      onComplete: () => flash.remove(),
    },
  );

  return new Promise((resolve) => {
    let landed = 0;
    for (let index = 0; index < count; index += 1) {
      const coin = document.createElement("i");
      coin.className = `quick-rip__cash-coin quick-rip__cash-coin--${plan.kinds[index]}`;
      coin.setAttribute("aria-hidden", "true");
      const face = document.createElement("img");
      face.alt = "";
      face.src = "/app/static/images/quick-rip/coin.svg";
      coin.append(face);
      coin.style.left = `${start.x}px`;
      coin.style.top = `${start.y}px`;
      document.body.append(coin);

      const scatter = (index - (count - 1) / 2) * 18;
      const launchAt = index * 0.075;
      let spin: gsap.core.Tween | null = null;
      const timeline = gsap.timeline({
        delay: launchAt,
        onComplete: () => {
          spin?.kill();
          coin.remove();
          cashMotionEvent("coin-land");
          cashMotionEvent("count");
          onStep?.(steps[index] ?? 0);
          landed += 1;
          if (landed === count) resolve();
        },
      });
      spin = gsap.to(face, {
        scaleX: 0.15,
        duration: 0.18,
        repeat: -1,
        yoyo: true,
        ease: "sine.inOut",
      });
      timeline
        .call(
          () => {
            cashMotionEvent("money_tick");
            playCoinTick(index);
          },
          undefined,
          0,
        )
        .fromTo(
          coin,
          { xPercent: -50, yPercent: -50, x: 0, y: 0, scale: 0.25, opacity: 0 },
          {
            xPercent: -50,
            yPercent: -50,
            x: scatter,
            y: -90 - (index % 3) * 18,
            scale: 1,
            opacity: 1,
            duration: 0.42,
            ease: "back.out(1.6)",
          },
        )
        .to(coin, {
          x: destination.x - start.x,
          duration: 0.5,
          ease: "power1.inOut",
        })
        .to(
          coin,
          {
            y: destination.y - start.y,
            scale: 0.35,
            opacity: 0,
            duration: 0.5,
            ease: "power2.in",
          },
          "<",
        );
    }
  });
}
